import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Scale, Search } from 'lucide-react';
import { usePopoverDismissOutside } from './usePopoverDismissOutside';
import { buildJudSearchLookupPrompt } from './portalJudSearchChip';
import {
  PORTAL_JUD_SEARCH_FIELD_MARKER,
  PORTAL_JUD_SEARCH_LAWYER_MARKER,
} from './portalLexJudJoin';

const PILL =
  'inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-slate-900/70 px-2.5 py-1 text-[11px] font-medium text-slate-300 hover:border-violet-500/30 hover:bg-violet-500/5 hover:text-slate-100 transition-colors';

const PILL_ACTIVE =
  'inline-flex items-center gap-1 rounded-full border border-violet-500/40 bg-violet-500/10 px-2.5 py-1 text-[11px] font-medium text-white ring-1 ring-violet-400/25';

const ACTION_BTN =
  'inline-flex items-center gap-1 rounded-md border border-white/[0.1] bg-slate-900/80 px-2 py-1 text-[10px] font-medium text-slate-200 hover:border-violet-500/35 hover:bg-violet-500/10 disabled:opacity-40 disabled:cursor-not-allowed';

export type ArkoComposerJudSearchChipProps = {
  onLookup: (query: string) => void;
  disabled?: boolean;
  className?: string;
};

/**
 * Eigenes Judikatur-Suchfeld — unabhängige Anwaltssuche, nicht Fall-Join.
 * Marker: portal-jud-search:own-field-v1 + lawyer-search-v1
 */
export const ArkoComposerJudSearchChip: React.FC<ArkoComposerJudSearchChipProps> = ({
  onLookup,
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prompt = buildJudSearchLookupPrompt(query);
  const close = useCallback(() => setOpen(false), []);

  usePopoverDismissOutside({ open, onClose: close, rootRef });

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  const runLookup = useCallback(() => {
    if (disabled || !prompt) return;
    onLookup(prompt);
    setOpen(false);
  }, [disabled, onLookup, prompt]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      runLookup();
    }
  };

  return (
    <div ref={rootRef} className={`relative inline-flex flex-col items-start gap-1 ${className}`}>
      <span className="sr-only">{PORTAL_JUD_SEARCH_FIELD_MARKER}</span>
      <span className="sr-only">{PORTAL_JUD_SEARCH_LAWYER_MARKER}</span>
      <button
        type="button"
        data-testid="portal-chip-judikatur"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="OGH-Judikatur suchen — unabhängige Suche, nicht an den Fall gekoppelt"
        className={`${open ? PILL_ACTIVE : PILL} disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        <Scale className="w-3 h-3 shrink-0" aria-hidden />
        <span>Judikatur</span>
        <span className="text-[10px] font-normal text-slate-500">Suche</span>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="OGH-Judikatur suchen — unabhängige Anwaltssuche"
          className="absolute bottom-full left-0 mb-1.5 z-40 w-[min(100vw-2rem,22rem)] rounded-xl border border-white/[0.1] bg-[#141619] shadow-[0_-8px_32px_rgba(0,0,0,0.45)] overflow-hidden"
        >
          <div className="p-2.5 space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 px-0.5">
              Suche
            </p>
            <label className="block">
              <span className="sr-only">Judikatur suchen</span>
              <div className="flex items-center gap-1.5 rounded-lg border border-white/[0.1] bg-slate-950/80 px-2 py-1.5 focus-within:border-violet-500/40">
                <Search className="w-3.5 h-3.5 text-slate-500 shrink-0" aria-hidden />
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  disabled={disabled}
                  placeholder="z. B. Befristung MRG oder 5 Ob 200/24"
                  autoComplete="off"
                  className="w-full bg-transparent text-[12px] text-slate-100 placeholder:text-slate-600 focus:outline-none"
                />
              </div>
            </label>
            <p className="text-[10px] text-slate-500 leading-snug px-0.5">
              Unabhängige Anwaltssuche in der Judikatur. Nicht an den Fall
              gekoppelt, nichts erfinden.
            </p>
            <button
              type="button"
              className={ACTION_BTN}
              disabled={disabled || !prompt}
              onClick={runLookup}
              title="OGH-Judikatur suchen — unabhängige Suche, nicht an den Fall gekoppelt"
            >
              <Search className="w-3 h-3 shrink-0" aria-hidden />
              Suchen
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
